import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Instagram, Facebook } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import useSettings from "../hooks/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  nome: "",
  telefono: "",
  email: "",
  data: "",
  ora: "19:30",
  ospiti: 2,
  note: "",
};

const inputCls =
  "w-full bg-[#0D0C0A] border border-[rgba(201,162,101,0.2)] rounded-xl px-4 py-3 text-sm text-[#FAF7F2] placeholder-[#7A7267] focus:outline-none focus:border-[#C9A265] transition-colors duration-300";

const DAYS = [
  [{ it: "Lunedì", en: "Monday" }, "12:30–14:00 / 19:30–22:00"],
  [{ it: "Martedì", en: "Tuesday" }, "12:30–14:00 / 19:30–22:00"],
  [{ it: "Mercoledì", en: "Wednesday" }, "closed"],
  [{ it: "Giovedì", en: "Thursday" }, "12:30–14:00 / 19:30–22:00"],
  [{ it: "Venerdì", en: "Friday" }, "12:30–14:00 / 19:30–22:00"],
  [{ it: "Sabato", en: "Saturday" }, "12:30–14:00 / 19:30–22:00"],
  [{ it: "Domenica", en: "Sunday" }, "12:30–14:00 / 19:30–22:00"],
];

const isWednesday = (dateStr) => {
  if (!dateStr) return false;
  return new Date(`${dateStr}T12:00:00`).getDay() === 3;
};

export default function Contatti() {
  const { lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const settings = useSettings();
  const wednesday = isWednesday(form.data);
  const specialClosure = blockedDates.includes(form.data);
  const dayClosed = wednesday || specialClosure;

  useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((r) => setBlockedDates(r.data?.blocked_dates || []))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (dayClosed) {
      toast.error(
        lang === "it"
          ? "Siamo chiusi in questa data: scegli un altro giorno."
          : "We're closed on this date: please pick another day."
      );
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/reservations`, { ...form, ospiti: Number(form.ospiti) });
      toast.success(
        <span data-testid="reservation-success-toast">
          {lang === "it"
            ? "Richiesta inviata! Ti confermeremo il tavolo a breve."
            : "Request sent! We will confirm your table shortly."}
        </span>
      );
      setForm(EMPTY);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        (lang === "it"
          ? "Qualcosa è andato storto. Riprova o chiamaci direttamente."
          : "Something went wrong. Please try again or call us directly.")
      );
    } finally {
      setSending(false);
    }
  };

  const L = (it, en) => (lang === "it" ? it : en);

  return (
    <section id="contatti" data-testid="contatti-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <span className="eyebrow">{L("Contatti & Prenotazioni", "Contacts & Reservations")}</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#FAF7F2]">
            {lang === "it" ? (
              <>Il tuo tavolo <span className="italic text-[#C9A265]">ti aspetta</span></>
            ) : (
              <>Your table <span className="italic text-[#C9A265]">awaits you</span></>
            )}
          </h2>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-5 gap-10">
          <motion.form
            data-testid="reservation-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-3 bg-[#171512] border border-[rgba(201,162,101,0.18)] rounded-2xl p-8 lg:p-10 grid sm:grid-cols-2 gap-5"
          >
            <div className="sm:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel mb-2">{L("Nome e Cognome", "Full Name")}</label>
              <input data-testid="reservation-form-name-input" required value={form.nome} onChange={set("nome")} placeholder={L("Mario Rossi", "John Smith")} className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel mb-2">{L("Telefono", "Phone")}</label>
              <input data-testid="reservation-form-phone-input" required type="tel" value={form.telefono} onChange={set("telefono")} placeholder="+39 333 000 0000" className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel mb-2">Email</label>
              <input data-testid="reservation-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="mario@email.it" className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel mb-2">{L("Data", "Date")}</label>
              <input data-testid="reservation-form-date-input" required type="date" value={form.data} onChange={set("data")} className={inputCls} />
              {wednesday && (
                <p data-testid="reservation-wednesday-warning" className="mt-2 text-[11px] uppercase tracking-[0.15em] text-[#D08068] font-cinzel">
                  {L("Il mercoledì siamo chiusi", "We're closed on Wednesdays")}
                </p>
              )}
              {specialClosure && !wednesday && (
                <p data-testid="reservation-closure-warning" className="mt-2 text-[11px] uppercase tracking-[0.15em] text-[#D08068] font-cinzel">
                  {L("Chiusura straordinaria in questa data", "Special closure on this date")}
                </p>
              )}
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel mb-2">{L("Ora", "Time")}</label>
              <select data-testid="reservation-form-time-select" value={form.ora} onChange={set("ora")} className={inputCls}>
                {["12:30", "13:00", "13:30", "19:30", "20:00", "20:30", "21:00"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel mb-2">{L("Ospiti", "Guests")}</label>
              <select data-testid="reservation-form-guests-select" value={form.ospiti} onChange={set("ospiti")} className={inputCls}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? L("ospite", "guest") : L("ospiti", "guests")}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel mb-2">{L("Richieste speciali", "Special Requests")}</label>
              <textarea data-testid="reservation-form-notes-input" rows={3} value={form.note} onChange={set("note")} placeholder={L("Allergie, occasioni speciali, seggiolone...", "Allergies, special occasions, high chair...")} className={inputCls} />
            </div>
            <button
              data-testid="reservation-form-submit-button"
              type="submit"
              disabled={sending || dayClosed}
              className="sm:col-span-2 mt-2 px-8 py-4 bg-[#C9A265] text-[#0D0C0A] text-xs uppercase tracking-[0.25em] font-semibold font-cinzel rounded-full hover:bg-[#E0B87A] hover:shadow-[0_0_40px_rgba(201,162,101,0.35)] transition-all duration-300 disabled:opacity-50"
            >
              {sending ? L("Invio in corso...", "Sending...") : L("Invia Richiesta di Prenotazione", "Send Reservation Request")}
            </button>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2 flex flex-col gap-6"
          >
            <div className="rounded-2xl overflow-hidden border border-[rgba(201,162,101,0.18)] h-64">
              <iframe
                data-testid="map-iframe"
                title="Mappa Ristorante Milano"
                src="https://www.openstreetmap.org/export/embed.html?bbox=8.4430%2C45.1320%2C8.4630%2C45.1420&layer=mapnik&marker=45.1370%2C8.4530"
                className="w-full h-full grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
              />
            </div>
            <div className="bg-[#171512] border border-[rgba(201,162,101,0.18)] rounded-2xl p-8 space-y-6">
              <div className="flex gap-4">
                <MapPin className="text-[#C9A265] shrink-0" size={20} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel">{L("Indirizzo", "Address")}</p>
                  <p className="mt-1 text-sm text-[#FAF7F2]" data-testid="contact-address">
                    Piazza Castello 41<br />15033 Casale Monferrato (AL)
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Phone className="text-[#C9A265] shrink-0" size={20} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel">{L("Telefono", "Phone")}</p>
                  <a href="tel:+393514369996" data-testid="contact-phone" className="mt-1 block text-sm text-[#FAF7F2] hover:text-[#C9A265] transition-colors">
                    +39 351 436 9996
                  </a>
                </div>
              </div>
              {(settings.instagram_url || settings.facebook_url) && (
                <div className="flex gap-4">
                  <Instagram className="text-[#C9A265] shrink-0" size={20} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel">Social</p>
                    <div className="mt-2 flex gap-3">
                      {settings.instagram_url && (
                        <a
                          href={settings.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="contact-instagram-link"
                          aria-label="Instagram"
                          className="h-10 w-10 rounded-full border border-[rgba(201,162,101,0.4)] flex items-center justify-center text-[#C9A265] hover:bg-[#C9A265] hover:text-[#0D0C0A] transition-colors duration-300"
                        >
                          <Instagram size={16} />
                        </a>
                      )}
                      {settings.facebook_url && (
                        <a
                          href={settings.facebook_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="contact-facebook-link"
                          aria-label="Facebook"
                          className="h-10 w-10 rounded-full border border-[rgba(201,162,101,0.4)] flex items-center justify-center text-[#C9A265] hover:bg-[#C9A265] hover:text-[#0D0C0A] transition-colors duration-300"
                        >
                          <Facebook size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-4">
                <Clock className="text-[#C9A265] shrink-0" size={20} />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel">{L("Orari", "Opening Hours")}</p>
                  <div className="mt-1 text-sm text-[#FAF7F2] space-y-1.5" data-testid="contact-hours">
                    {DAYS.map(([day, hours]) => (
                      <p key={day.it} className="flex justify-between gap-4">
                        <span className="text-[#B0A89C]">{day[lang]}</span>
                        <span className={hours === "closed" ? "text-[#7A7267]" : ""}>
                          {hours === "closed" ? L("Chiuso", "Closed") : hours}
                        </span>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
